import { Search, Menu, Moon, Sun, Bookmark, Play, Video, ChevronUp, Image as ImageIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { useTheme } from './ThemeProvider';
import { bloggerService } from '../services/blogger';
import { extractFirstImage } from '../lib/utils';
import { useGlobals } from '../lib/useGlobals';

interface HeaderProps {
  title?: string;
  onMenuClick?: () => void;
}

export function Header({ title = "Seba Hot News", onMenuClick }: HeaderProps) {
  const { theme, toggleTheme } = useTheme();
  const { globals } = useGlobals();

  // Handle custom top categories from globals if specified, otherwise fetch latest popular
  let topCats: string[] = [];
  try {
     if (globals.headerTopCategories) {
       topCats = JSON.parse(globals.headerTopCategories);
       if (!Array.isArray(topCats)) topCats = [];
     }
  } catch(e) {}

  const hasTopCats = topCats.length > 0;

  // Custom fetcher that either fetches 3 posts from 3 different categories or 3 popular posts
  const fetcher = async () => {
    if (hasTopCats) {
      const posts = await Promise.all(
        topCats.slice(0, 3).map(cat => bloggerService.getPostsByLabel(cat, 1).then(res => res.items?.[0]))
      );
      return { items: posts.filter(Boolean) };
    }
    return bloggerService.getPosts(undefined, 3);
  };

  const { data: headerPostsData } = useSWR(hasTopCats ? `header_cats_${topCats.join('_')}` : 'popular_posts_header', fetcher);

  // Determine logo to use
  const logoUrl = globals.headerLogoUrl || '/logo.png';

  return (
    <header className="bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 px-4 lg:px-8 pt-[10px] pb-[10px] flex items-center justify-between relative z-40">
      <div className="flex items-center gap-4 w-full justify-between lg:w-auto lg:justify-start lg:shrink-0">
        <button 
          onClick={onMenuClick}
          className="p-2 -ml-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full text-[#111111] dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 active:scale-95 transition-transform lg:hidden shrink-0" 
          aria-label="Menu"
        >
          <Menu size={24} />
        </button>
        
        {/* Mobile Logo */}
        <Link to="/" className="flex items-center active:scale-95 transition-transform absolute left-1/2 -translate-x-1/2 lg:hidden">
          <img 
            src={logoUrl} 
            alt={title} 
            width="150"
            height="40"
            className="h-10 w-auto object-contain drop-shadow-sm"
            onError={(e) => {
              e.currentTarget.src = 'https://blogger.googleusercontent.com/img/a/AVvXsEjUwTZQvWJ5mtwWoSrDl1gZ4cNy8KJjGmEDuUX2FArP3-SzeRIg0XUCVlOft6wFwf-HomJ6irpfrJ3LL_nykDZ53j40TKM0Yf0v_IDsFeLQly2Pdqzi0BVIuITlczIzcdmoOgPpEEwt5qJ_PVP3GgheTTZKTAPoZ3CZryX0O4C8Uzm8CPdXAnjPIk2fQe8S=s600';
            }}
          />
        </Link>

        {/* Desktop Logo */}
        <Link to="/" className="hidden lg:flex items-center shrink-0 active:scale-95 transition-transform">
          <img 
            src={logoUrl} 
            alt={title} 
            width="180"
            height="48"
            className="h-12 w-auto object-contain drop-shadow-sm"
            onError={(e) => {
              e.currentTarget.src = 'https://blogger.googleusercontent.com/img/a/AVvXsEjUwTZQvWJ5mtwWoSrDl1gZ4cNy8KJjGmEDuUX2FArP3-SzeRIg0XUCVlOft6wFwf-HomJ6irpfrJ3LL_nykDZ53j40TKM0Yf0v_IDsFeLQly2Pdqzi0BVIuITlczIzcdmoOgPpEEwt5qJ_PVP3GgheTTZKTAPoZ3CZryX0O4C8Uzm8CPdXAnjPIk2fQe8S=s600';
            }}
          />
        </Link>

        {/* Mobile Search - balances flex-between */}
        <Link to="/search" className="p-2 -mr-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full text-[#111111] dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 active:scale-95 transition-transform lg:hidden shrink-0" aria-label="Search">
          <Search size={24} />
        </Link>
      </div>
      
      {/* Desktop Popular Posts Section */}
      <div className="hidden lg:flex flex-1 mx-8 items-center justify-center overflow-hidden max-w-[950px] gap-2 lg:gap-3">
        {headerPostsData?.items?.map((post: any, index: number) => {
          let paddingClass = "";
          if (index === 1) paddingClass = "pl-0";
          if (index === 2) paddingClass = "pl-0";

          return (
            <div key={post.id} className={`flex items-center gap-2 w-[310px] ${paddingClass}`}>
              <Link to={post.url ? new URL(post.url).pathname : `/post/${post.id}`} className="shrink-0 group overflow-hidden bg-gray-100 dark:bg-gray-800">
                <img 
                  src={extractFirstImage(post.content) || 'https://images.unsplash.com/photo-1542281286-9e0a16bb7366?auto=format&fit=crop&w=150&h=150'} 
                  alt={post.title} 
                  className="w-[50px] h-[50px] object-cover group-hover:scale-110 transition-transform duration-300"
                />
              </Link>
              <Link to={post.url ? new URL(post.url).pathname : `/post/${post.id}`} className="flex-1 min-w-0 group cursor-pointer">
                <h4 className="text-[13px] font-bengali font-medium group-hover:text-[#D32F2F] text-gray-900 dark:text-gray-100 transition-colors line-clamp-2 leading-[1.4]">
                  {post.title}
                </h4>
              </Link>
            </div>
          );
        })}
      </div>
    </header>
  );
}
